import React, { useState } from 'react'
import Header from './components/Header'
import AuthModal from './components/AuthModal'
import ClassCard from './components/ClassCard'
import AddClassModal from './components/AddClassModal'
import SafeIcon from './common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { useAuth } from './hooks/useAuth'
import { useClasses } from './hooks/useClasses'
import { generateClassSchedule } from './utils/classSchedule'

const { FiPlus, FiLoader, FiAlertCircle } = FiIcons

function App() {
  const [showAddModal, setShowAddModal] = useState(false)
  const { isAuthenticated, showAuthModal, setShowAuthModal, authenticate, logout, requestAuth } = useAuth()
  const { classes, loading, error, addClass, updateClass, deleteClass } = useClasses()

  const classSchedule = generateClassSchedule()

  const handleAddClass = async (classData) => {
    try {
      return await addClass(classData)
    } catch (error) {
      console.error('Error in handleAddClass:', error)
      return { success: false, error: error.message }
    }
  }

  const handleUpdateClass = async (classId, updates) => {
    try {
      if (!classId) {
        // If no classId, this is a new class
        return await addClass(updates)
      }
      return await updateClass(classId, updates)
    } catch (error) {
      console.error('Error in handleUpdateClass:', error)
      return { success: false, error: error.message }
    }
  }

  const handleDeleteClass = async (classId) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      try {
        return await deleteClass(classId)
      } catch (error) {
        console.error('Error in handleDeleteClass:', error)
        return { success: false, error: error.message }
      }
    }
    return { success: false }
  }

  const getClassData = (classNumber) => {
    try {
      return classes.find(cls => cls.class_number === classNumber)
    } catch (error) {
      console.error('Error finding class data:', error)
      return null
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiLoader} className="text-blue-500 text-2xl animate-spin" />
          <span className="text-gray-600">Loading classes...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <SafeIcon icon={FiAlertCircle} className="text-red-500 text-4xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-4">Unable to load class data: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        isAuthenticated={isAuthenticated} 
        onLogout={logout} 
        onRequestAuth={requestAuth} 
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Class Recordings</h2>
            <p className="text-gray-600 mt-1">
              Access your Quran classes, recordings, and notes
            </p>
          </div>
          
          {isAuthenticated && (
            <button 
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <SafeIcon icon={FiPlus} />
              <span>Add New Class</span>
            </button>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {classSchedule.slice(0, 50).map((scheduleInfo) => {
            const classData = getClassData(scheduleInfo.classNumber)
            return (
              <ClassCard
                key={scheduleInfo.classNumber}
                classData={classData}
                scheduleInfo={scheduleInfo}
                isAuthenticated={isAuthenticated}
                onUpdate={handleUpdateClass}
                onDelete={handleDeleteClass}
              />
            )
          })}
        </div>

        {classSchedule.length > 50 && (
          <div className="mt-8 text-center">
            <p className="text-gray-500">
              Showing first 50 classes. Total: {classSchedule.length} classes
            </p>
          </div>
        )}
      </main>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        onAuthenticate={authenticate} 
      />
      
      <AddClassModal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)} 
        onAdd={handleAddClass} 
      />
    </div>
  )
}

export default App